import { Box, Image, Text, Tooltip, Stack } from "@mantine/core";
import { useState, useEffect, useMemo } from "react";
import type { CSSProperties } from "react";
import { organMapTemplatesService } from "../../services/admin/organMapTemplates.service";
import { REPORT_IMAGE_ROLE } from "../../utils/reportImageAssets";
import { partitionOrganMapMarkers } from "../../utils/reportOrganMapCoordinates";
import { MarkerSvg } from "./MarkerSvg";
import { ReportMediaFrame } from "./ReportMediaFrame";

export { MarkerSvg } from "./MarkerSvg";

/**
 * A specimen marker to render on the organ map diagram.
 * Position comes from the specimen's subsite (positionX/Y as % of image).
 * Marker shape/color comes from the specimen's diagnosis category.
 */
export interface OrganMapMarker {
  specimenId: string;
  specimenLabel: string;
  sourceName?: string | null;
  subsiteName: string | null;
  positionX: number | null;
  positionY: number | null;
  categoryName: string | null;
  markerShape: string | null;
  markerColor: string | null;
}

type UnpositionedSpecimenDisplay = "visible" | "hidden";

interface OrganMapDiagramProps {
  /** S3 key or URL of the organ map template image */
  imageUrl: string;
  /** Template image dimensions (for aspect ratio) */
  width?: number | null;
  height?: number | null;
  /** Label shown above the diagram */
  label?: string;
  /** Specimen markers to place on the diagram */
  markers: OrganMapMarker[];
  /** Callback when a marker is clicked */
  onMarkerClick?: (specimenId: string) => void;
  /** Compact mode (no outer border) */
  compact?: boolean;
  /** Fixed image height in points (pt) — width auto-scales from aspect ratio */
  imageHeight?: number;
  /** Fixed report width in points; height follows the template aspect ratio. */
  imageWidth?: string;
  /** Shared row height used to bottom-align report media. */
  imageFrameHeight?: CSSProperties["height"];
  /** Controls whether unresolved placement diagnostics are shown. */
  unpositionedSpecimenDisplay?: UnpositionedSpecimenDisplay;
}

export default function OrganMapDiagram({
  imageUrl,
  width,
  height,
  label,
  markers,
  onMarkerClick,
  compact = false,
  imageHeight,
  imageWidth,
  imageFrameHeight,
  unpositionedSpecimenDisplay = "visible",
}: OrganMapDiagramProps) {
  const directImageUrl = useMemo(
    () => (/^(?:https?:|data:|blob:)/i.test(imageUrl) ? imageUrl : null),
    [imageUrl]
  );
  const [downloadedImage, setDownloadedImage] = useState<{
    key: string;
    url: string;
  } | null>(null);
  const [failedImageKey, setFailedImageKey] = useState<string | null>(null);
  const resolvedImageUrl = useMemo(
    () =>
      directImageUrl ||
      (downloadedImage?.key === imageUrl ? downloadedImage.url : null),
    [directImageUrl, downloadedImage, imageUrl]
  );

  useEffect(() => {
    let isCurrentImage = true;

    if (directImageUrl) setFailedImageKey(null);
    if (imageUrl && !directImageUrl) {
      organMapTemplatesService
        .getDownloadUrl(imageUrl)
        .then((res) => {
          if (isCurrentImage) {
            setDownloadedImage({ key: imageUrl, url: res.url });
            setFailedImageKey(null);
          }
        })
        .catch(() => {
          if (isCurrentImage) {
            setDownloadedImage(null);
            setFailedImageKey(imageUrl);
          }
        });
    }

    return () => {
      isCurrentImage = false;
    };
  }, [directImageUrl, imageUrl]);

  const { positioned: positionedMarkers, unpositioned: unpositionedMarkers } =
    useMemo(() => partitionOrganMapMarkers(markers), [markers]);

  if (!resolvedImageUrl) {
    return imageUrl ? (
      <Box
        data-report-layout-pending="true"
        data-report-layout-failed={
          failedImageKey === imageUrl ? "true" : undefined
        }
      />
    ) : null;
  }

  // Use aspect ratio from template dimensions for proper scaling
  const imgWidth = width || 500;
  const imgHeight = height || 400;
  const aspectRatio = imgWidth / imgHeight;

  // Collect unique categories for the legend
  const usedCategories = new Map<
    string,
    { name: string; shape: string | null; color: string | null }
  >();
  positionedMarkers.forEach((m) => {
    if (m.categoryName && !usedCategories.has(m.categoryName)) {
      usedCategories.set(m.categoryName, {
        name: m.categoryName,
        shape: m.markerShape,
        color: m.markerColor,
      });
    }
  });

  const markerSize = imageHeight || imageWidth ? 16 : compact ? 22 : 26;

  return (
    <Box
      mt={label ? 16 : 0}
      style={{
        breakInside: "avoid",
        pageBreakInside: "avoid",
        ...(imageHeight || imageWidth ? { width: "fit-content" } : {}),
      }}
    >
      {label && (
        <Text
          fw={700}
          size="sm"
          mb={8}
          style={{ letterSpacing: 1, borderBottom: "1px solid #333" }}
        >
          {label}
        </Text>
      )}

      <ReportMediaFrame height={imageFrameHeight}>
        <Box
          pos="relative"
          style={{
            height: imageHeight ? `${imageHeight}pt` : undefined,
            width:
              imageWidth ||
              (imageHeight ? `${imageHeight * aspectRatio}pt` : "100%"),
            margin: imageHeight || imageWidth ? "0 auto" : undefined,
            aspectRatio: `${aspectRatio}`,
            border:
              compact || imageHeight || imageWidth
                ? undefined
                : "1px solid var(--mantine-color-gray-3)",
            borderRadius: compact || imageHeight || imageWidth ? undefined : 4,
            overflow: "hidden",
          }}
        >
          <Image
            src={resolvedImageUrl}
            alt={label || "Organ map"}
            data-report-image-role={REPORT_IMAGE_ROLE.organMap}
            w="100%"
            h="100%"
            fit="contain"
          />

          {/* Render specimen markers at their subsite positions */}
          {positionedMarkers.map((marker) => (
            <Tooltip
              key={marker.specimenId}
              label={
                <Stack gap={2}>
                  <Text size="xs" fw={600}>
                    Specimen {marker.specimenLabel}
                  </Text>
                  {marker.subsiteName && (
                    <Text size="xs">{marker.subsiteName}</Text>
                  )}
                  {marker.categoryName && (
                    <Text size="xs" c="dimmed">
                      {marker.categoryName}
                    </Text>
                  )}
                </Stack>
              }
              withArrow
            >
              <Box
                pos="absolute"
                style={{
                  left: `calc(${marker.positionX}% - ${markerSize / 2}px)`,
                  top: `calc(${marker.positionY}% - ${markerSize / 2}px)`,
                  cursor: onMarkerClick ? "pointer" : "default",
                  zIndex: 5,
                  filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
                }}
                onClick={
                  onMarkerClick
                    ? () => onMarkerClick(marker.specimenId)
                    : undefined
                }
              >
                <MarkerSvg
                  shape={marker.markerShape}
                  color={marker.markerColor}
                  size={markerSize}
                />
                {/* Specimen label overlay */}
                <Text
                  size="10px"
                  fw={700}
                  c="white"
                  ta="center"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textShadow: "0 0 2px rgba(0,0,0,0.8)",
                    pointerEvents: "none",
                    lineHeight: 1,
                  }}
                >
                  {marker.specimenLabel}
                </Text>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </ReportMediaFrame>

      {unpositionedSpecimenDisplay === "visible" &&
        unpositionedMarkers.length > 0 && (
          <Text
            mt={4}
            size="xs"
            c="dimmed"
            data-testid="unpositioned-organ-map-specimens"
            style={{ lineHeight: 1.25 }}
          >
            Unpositioned specimens:{" "}
            {unpositionedMarkers
              .map((marker) => {
                const source = marker.sourceName || marker.subsiteName;
                return `${marker.specimenLabel}${source ? ` (${source})` : ""}`;
              })
              .join(", ")}
          </Text>
        )}

      {/* Legend — grid: row 1 = all labels, row 2 = all icons */}
      {usedCategories.size > 0 &&
        (() => {
          const cats = Array.from(usedCategories.values());
          const TN = "'Times New Roman', Times, serif";
          const cols = `auto ${cats.map(() => "auto").join(" ")}`;
          return (
            <Box
              mt={4}
              style={{
                display: "inline-grid",
                gridTemplateColumns: cols,
                columnGap: "8pt",
                rowGap: "2px",
              }}
            >
              <span
                style={{
                  fontFamily: TN,
                  fontSize: "9pt",
                  color: "#000000",
                  lineHeight: 1.15,
                  whiteSpace: "nowrap",
                  alignSelf: "baseline",
                }}
              >
                Map Legend:
              </span>
              {cats.map((cat) => (
                <span
                  key={`lbl-${cat.name}`}
                  style={{
                    fontFamily: TN,
                    fontSize: "7pt",
                    color: "#000000",
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    alignSelf: "baseline",
                  }}
                >
                  {cat.name}
                </span>
              ))}
              <span />
              {cats.map((cat) => (
                <Box
                  key={`ico-${cat.name}`}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <MarkerSvg shape={cat.shape} color={cat.color} size={22} />
                </Box>
              ))}
            </Box>
          );
        })()}
    </Box>
  );
}
